import { useCallback, useMemo, useState } from 'react';
import filter from 'lodash/filter';
import get from 'lodash/get';
import size from 'lodash/size';
import map from 'lodash/map';

import {
  ChangeProductsFiltersFunc,
  FetchProductsFilterFields,
  FetchProductsFilters
} from '../../../../../../products/productsTypes';
import { CacheFetchProductsAggregationsQueryResponseAggregations } from '../../../../../../products/queries/fetchCacheProductsAggregations.query';
import { IconsEnum } from '../../../../../../../assets/icons/types';

import { ThreeDStockProductsFiltersColorsFilterItem } from './components/ThreeDStockProductsFiltersColorsFilterItem';

import { ButtonHelper } from '../../../../../../../helpers/buttons/ButtonHelper';
import { Icon } from '../../../../../../../helpers/Icon';
import { Translate } from '../../../../../../../helpers/Translate';

import { groupByBaseColors } from '../../../../../utils/groupByBaseColors';

import { categoriesKeys } from '../../../../../../../locales/keys';

const fieldName = FetchProductsFilterFields.AI_MAIN_COLOR_TAGS;

interface ThreeDStockProductsFiltersColorsFilterProps {
  productsAggregations: CacheFetchProductsAggregationsQueryResponseAggregations;
  productsFilters: FetchProductsFilters;
  changeProductsFilters: ChangeProductsFiltersFunc;
}

function ThreeDStockProductsFiltersColorsFilter({
  productsAggregations,
  changeProductsFilters,
  productsFilters
}: ThreeDStockProductsFiltersColorsFilterProps) {
  const [isOpen, setIsOpen] = useState<boolean>(true);

  const toggleIsOpen = useCallback<() => void>(
    () => setIsOpen((prevState) => !prevState),
    []
  );

  const tags = map(
    get(productsAggregations, ['ai_main_color_tags', 'en']),
    'name'
  );

  const checkedTags: string[] = useMemo(
    () => productsFilters[fieldName]?.in || [],
    [productsFilters]
  );

  const groupedColors = groupByBaseColors(tags);

  const onCheckedColor = useCallback(
    (colors: string[], checked: boolean) => {
      const newTags = checked
        ? [...checkedTags, ...colors]
        : filter(checkedTags, (tag) => !colors.includes(tag));

      changeProductsFilters(
        {
          [fieldName]: {
            in: newTags
          }
        },
        size(newTags) > 0 ? [] : [fieldName]
      );
    },
    [checkedTags, changeProductsFilters]
  );

  const selectedBaseColors = useMemo<string[]>(() => {
    const baseColors = [];

    if (checkedTags.length === 0) {
      return baseColors;
    }

    const checkedTagSet = new Set(checkedTags);

    for (const { baseColor, colors } of groupedColors) {
      if (colors?.every((color) => checkedTagSet.has(color))) {
        baseColors.push(baseColor);
      }
    }

    return baseColors;
  }, [checkedTags, groupedColors]);

  if (size(tags) === 0) {
    return null;
  }

  return (
    <div className="p-4">
      <div className="flex flex-col gap-3">
        <ButtonHelper
          className="flex items-center gap-2 rounded-sm"
          dataGa={`${fieldName}-ai_tags_filter`}
          onClick={toggleIsOpen}
        >
          <span className="text-sm font-semibold text-left flex-1">
            <Translate id={categoriesKeys.colors.plural} />
          </span>

          <Icon
            icon={isOpen ? IconsEnum.CHEVRON_DOWN_SOLID : IconsEnum.CHEVRON_UP}
            className="h-5 w-5 text-gray-500"
          />
        </ButtonHelper>

        <div className="color-filter-content">
          {isOpen && (
            <div className="grid grid-cols-auto-fill grid-cell-min-8 gap-2">
              {groupedColors?.map((groupedColor) => (
                <ThreeDStockProductsFiltersColorsFilterItem
                  key={groupedColor.baseColor}
                  groupedColor={groupedColor}
                  onCheckedColor={onCheckedColor}
                  checked={selectedBaseColors.includes(groupedColor.baseColor)}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ThreeDStockProductsFiltersColorsFilter;
